
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTestData } from "./useTestData";
import { TestResponse, FilterType } from "@/types/test-response";
import { SortOption } from "@/components/test-details/responses-table/SortAndSearchBar";
import { sortResponsesByAIScore } from "@/utils/response-utils";

export const usePaginatedTestResponses = (testId?: string) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1");
  const pageSize = 30;
  
  // Get state from URL params to persist across navigation
  const sortOption = (searchParams.get("sort") as SortOption) || "highestScore";
  const activeFilters = searchParams.get("filters") 
    ? (searchParams.get("filters")!.split(",") as FilterType[])
    : [];
  
  // Use refs to track previous values and prevent unnecessary resets
  const prevActiveFilters = useRef(activeFilters);
  const prevSortOption = useRef(sortOption);
  
  const {
    useResponsesQuery,
    useAllResponsesQuery,
    isLoadingTest,
    isLoadingQuestions,
  } = useTestData(testId);

  // Determine if we need client-side pagination (for AI score sorting)
  const needsClientSidePagination = sortOption === "highestScore" || sortOption === "lowestScore" || sortOption === "aiScore";

  // Fetch responses with server-side pagination (for non-AI score sorting)
  const {
    data: serverResponsesResult = { data: [], total: 0 },
    isLoading: isLoadingServerResponses,
    error: serverResponsesError,
    refetch: refetchServerResponses,
  } = useResponsesQuery(
    currentPage,
    pageSize,
    activeFilters,
    sortOption
  );

  // Fetch ALL responses for client-side AI score sorting
  const {
    data: allResponses = [],
    isLoading: isLoadingAllResponses,
    error: allResponsesError,
    refetch: refetchAllResponses,
  } = useAllResponsesQuery(
    activeFilters
  );

  // Client-side sorting and pagination for AI scores
  const clientSidePaginatedResponses = useMemo(() => {
    if (!needsClientSidePagination || !allResponses.length) {
      return { data: [], total: 0 };
    }

    // Sort all responses by AI score
    const sortedResponses = sortResponsesByAIScore(
      allResponses, 
      sortOption === "lowestScore"
    );

    // Apply client-side pagination
    const from = (currentPage - 1) * pageSize;
    const to = from + pageSize;
    const paginatedData = sortedResponses.slice(from, to);

    return {
      data: paginatedData,
      total: sortedResponses.length,
    };
  }, [allResponses, sortOption, currentPage, pageSize, needsClientSidePagination]);

  // Choose the appropriate data source based on sorting method
  const responsesQueryResult = needsClientSidePagination 
    ? clientSidePaginatedResponses 
    : serverResponsesResult;

  const isLoadingResponses = needsClientSidePagination 
    ? isLoadingAllResponses 
    : isLoadingServerResponses;

  const responsesError = needsClientSidePagination 
    ? allResponsesError 
    : serverResponsesError;

  // Unified refetch function
  const refetchResponses = useCallback(() => {
    if (needsClientSidePagination) {
      refetchAllResponses();
    } else {
      refetchServerResponses();
    }
  }, [needsClientSidePagination, refetchAllResponses, refetchServerResponses]);

  // When filters change, reset to page 1
  useEffect(() => {
    const filtersChanged = JSON.stringify(prevActiveFilters.current) !== JSON.stringify(activeFilters);
    const sortChanged = prevSortOption.current !== sortOption;
    
    // Only reset to page 1 if criteria actually changed AND we're not already on page 1
    if ((filtersChanged || sortChanged) && currentPage !== 1) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("page", "1");
        return newParams;
      });
    }
    
    // Update refs to current values
    prevActiveFilters.current = activeFilters;
    prevSortOption.current = sortOption;
  }, [activeFilters, sortOption, currentPage, setSearchParams]);

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page.toString());
      return newParams;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };


  const handleSortChange = (option: SortOption) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("sort", option);
      return newParams;
    });
  };

  const handleFiltersChange = (filters: FilterType[]) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (filters.length > 0) {
        newParams.set("filters", filters.join(","));
      } else {
        newParams.delete("filters");
      }
      return newParams;
    });
  };

  const totalPages = Math.max(1, Math.ceil(responsesQueryResult.total / pageSize));

  return {
    responses: responsesQueryResult.data || [],
    totalResponses: responsesQueryResult.total,
    currentPage,
    totalPages,
    pageSize,
    isLoading: isLoadingTest || isLoadingQuestions || isLoadingResponses,
    isError: !!responsesError,
    error: responsesError,
    activeFilters,
    setActiveFilters: handleFiltersChange,
    sortOption,
    handleSortChange,
    handlePageChange,
    refetchResponses,
    // Expose additional state for debugging/monitoring
    needsClientSidePagination,
    isLoadingAllData: isLoadingAllResponses,
  };
};
